import {
  useEffect, useRef, useState,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode,
} from 'react'

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex flex-col items-center gap-3" role="status">
      <div
        className="size-8 animate-spin rounded-full border-3 border-line border-t-graphite"
      />
      {label && <span className="label">{label}</span>}
    </div>
  )
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'md' | 'lg'
  loading?: boolean
}

export function Button({
  variant = 'secondary', size = 'md', loading, children, className = '', disabled, ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      className={`btn btn-${variant} ${size === 'lg' ? 'btn-lg' : ''} ${className}`}
      disabled={disabled || loading}
      {...rest}
    >
      {loading && (
        <span
          className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden
        />
      )}
      {children}
    </button>
  )
}

export function Field({
  label, hint, error, children, required,
}: {
  label: string
  hint?: string
  error?: string | null
  children: ReactNode
  required?: boolean
}) {
  return (
    <label className="block">
      <span className="label mb-1.5 block">
        {label}
        {required && <span className="text-alarm"> *</span>}
      </span>
      {children}
      {hint && !error && <span className="mt-1 block text-xs text-graphite-2">{hint}</span>}
      {error && <span className="mt-1 block text-xs font-semibold text-alarm">{error}</span>}
    </label>
  )
}

/**
 * A numeric field you can actually type in.
 *
 * The obvious spelling — `value={n}` with `onChange={e => set(Number(e.target.value))}`
 * — is unusable. Clearing it makes `Number('')` zero, so the field snaps back to
 * "0" and the only way to enter a new figure is to select the old one and type
 * over it. Worse for decimals: `Number('43.')` is `43`, so the point is eaten
 * the moment it is typed and a coordinate can never be entered at all.
 *
 * The fix is to let the typed text be the source of truth while the field has
 * focus, and only reconcile with the parent's value on blur. Intermediate states
 * ('', '-', '43.') are held as typed and reported as null rather than coerced.
 * Clamping also waits for blur — snapping "1" up to a minimum of 20 while
 * somebody is on their way to typing "150" is its own kind of hostile.
 */
export function NumberInput({
  value, onChange, min, max, step, className = '', decimal = false, ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  value: number | null | undefined
  onChange: (value: number | null) => void
  min?: number
  max?: number
  step?: number
  /** Allow a decimal point and a leading minus. */
  decimal?: boolean
}) {
  const asText = (v: number | null | undefined) => (v == null ? '' : String(v))
  const [text, setText] = useState(() => asText(value))
  const focused = useRef(false)

  // Track the parent's value, but never overwrite what is being typed.
  useEffect(() => {
    if (!focused.current) setText(asText(value))
  }, [value])

  const parse = (raw: string): number | null => {
    const trimmed = raw.trim()
    if (trimmed === '' || trimmed === '-' || trimmed === '.' || trimmed === '-.') return null
    const n = Number(trimmed)
    return Number.isFinite(n) ? n : null
  }

  return (
    <input
      type="text"
      inputMode={decimal ? 'decimal' : 'numeric'}
      className={`field ${className}`}
      value={text}
      onFocus={() => {
        focused.current = true
      }}
      onChange={(e) => {
        const raw = e.target.value
        // Reject characters that could never form a number, so the field cannot
        // be left holding something it will silently discard later.
        const allowed = decimal ? /^-?\d*\.?\d*$/ : /^\d*$/
        if (!allowed.test(raw)) return
        setText(raw)
        onChange(parse(raw))
      }}
      onBlur={() => {
        focused.current = false
        const n = parse(text)
        if (n == null) {
          setText('')
          onChange(null)
          return
        }
        let settled = n
        if (min != null) settled = Math.max(min, settled)
        if (max != null) settled = Math.min(max, settled)
        setText(String(settled))
        if (settled !== value) onChange(settled)
      }}
      {...rest}
    />
  )
}

/** Bottom sheet. Everything modal on the phone arrives from the bottom. */
export function Sheet({
  open, onClose, title, children, footer,
}: {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  footer?: ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  // Most callers pass an inline arrow, so onClose is a new function on every
  // parent render. Reading it through a ref keeps the effect below tied to
  // open/close only: re-running it per render re-focused the dialog on every
  // keystroke in a parent-owned field, which pulled focus out of the textarea
  // and dropped the phone keyboard after each letter.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current()
    }
    document.addEventListener('keydown', onKey)
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    ref.current?.focus()
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previous
    }
  }, [open])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center sm:items-center">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-graphite/60"
        onClick={onClose}
      />
      <div
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative flex max-h-[92dvh] w-full max-w-lg flex-col border border-line bg-raised shadow-2xl sm:rounded-lg"
      >
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <h2 className="text-lg">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost -mr-2 min-h-10 px-2 text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4">{children}</div>
        {footer && (
          <div className="safe-bottom border-t border-line px-4 py-3">{footer}</div>
        )}
      </div>
    </div>
  )
}

export function Chip({
  color, children, filled = true,
}: {
  color: string
  children: ReactNode
  filled?: boolean
}) {
  return (
    <span
      className="chip"
      style={
        filled
          ? { background: color, color: readableInk(color) }
          : { color, boxShadow: `inset 0 0 0 1px ${color}` }
      }
    >
      {children}
    </span>
  )
}

/** Pick black or white text for a background, by relative luminance. */
export function readableInk(hex: string): string {
  const clean = hex.replace('#', '')
  if (clean.length < 6) return '#ffffff'
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(clean.slice(i, i + 2), 16) / 255)
  const lum = 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
  return lum > 0.45 ? '#231f20' : '#ffffff'
}

function channel(c: number): number {
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

export function EmptyState({
  title, body, action,
}: {
  title: string
  body: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-3 border border-dashed border-line-strong px-6 py-10 text-center">
      <p className="text-base font-bold">{title}</p>
      <p className="max-w-xs text-sm text-graphite-2">{body}</p>
      {action}
    </div>
  )
}

export function ErrorNote({ children }: { children: ReactNode }) {
  if (!children) return null
  return (
    <p
      role="alert"
      className="border-l-3 border-alarm bg-alarm/8 px-3 py-2 text-sm font-semibold text-alarm"
    >
      {children}
    </p>
  )
}

/**
 * Wrapper for every data table.
 *
 * A `.grid-table` sizes to its content, and content on the admin side is
 * unbounded — long item names, long crew names, a column per reminder. Without
 * a scroll container the table sets a floor on the page width and the whole app
 * gains a horizontal scrollbar. This makes that structurally impossible.
 */
export function TableScroll({ children }: { children: ReactNode }) {
  return <div className="table-scroll">{children}</div>
}

/**
 * Marks the start of a section: an ink title with a rule running out to the
 * edge of the column, and any action tucked in after it.
 *
 * The rule is what does the work. It sits on the title's own line, so a section
 * break reads unmistakably without taking any more vertical space than the old
 * caption did — which matters on a phone showing four sections at once.
 */
/**
 * A bounded section: title in a card header, content on the raised surface.
 *
 * The rule-to-the-edge SectionHeading works on the worker screens, which have
 * two or three airy sections. The dense admin tabs drowned in it — heading
 * rules, table-header rules and row rules are all horizontal ink, so nothing
 * grouped. A card separates by surface instead: the eye finds the block, not
 * another line.
 */
export function SectionCard({
  title, action, footer, flush = false, children,
}: {
  title: ReactNode
  action?: ReactNode
  footer?: ReactNode
  /** Content touches the card edges (tables); default padding otherwise. */
  flush?: boolean
  children: ReactNode
}) {
  return (
    <section className="card overflow-hidden">
      <header className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-line px-4 py-3">
        <h2 className="section-title">{title}</h2>
        {action}
      </header>
      <div className={flush ? '' : 'space-y-4 p-4'}>{children}</div>
      {footer && <div className="border-t border-line bg-well/40 px-4 py-3">{footer}</div>}
    </section>
  )
}

export function SectionHeading({
  children, action,
}: {
  children: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="mt-1 mb-3 flex items-center gap-3">
      <h2 className="section-title">{children}</h2>
      <span className="section-rule" aria-hidden />
      {action}
    </div>
  )
}
